import * as Yup from "yup"

import { toEnumValue } from "../../../../../lib/lang"
import { Allergen } from "../../../admin/products/productsTypes"
import { AppLocalization } from "../../../../localization/appLocalization"
import { DaysOfWeek, DaysOfWeekType } from "../menuPlanningTypes"

import { SaveMenuTemplatePayload } from "./menuTemplatesApi"
import {
    CategoryFormValues,
    DishFormValues,
    DishParameter,
    DishParameterKey,
    DishParameterType,
    MenuTemplateCategory,
    MenuTemplateDetails,
    MenuTemplateDish,
    MenuTemplateFormValues,
} from "./menuTemplatesTypes"

const WEEKDAYS: DaysOfWeekType[] = [
    DaysOfWeek.MONDAY,
    DaysOfWeek.TUESDAY,
    DaysOfWeek.WEDNESDAY,
    DaysOfWeek.THURSDAY,
    DaysOfWeek.FRIDAY,
]

const ALLERGEN_SEPARATOR = ","

/** Locked and Prefilled inputs must be filled in the template; other modes may carry an optional default. */
export function isTemplateValueRequired(type: DishParameterType | string): boolean {
    return type === DishParameterType.LOCKED || type === DishParameterType.PREFILLED
}

export function emptyTemplateFormValues(): MenuTemplateFormValues {
    return { name: "", channels: [], categories: [] }
}

// Shared by the template form (nested under categories) and the dish drill-in form (standalone).
export function buildDishValidationSchema(requiredMessage: string, numberMessage: string) {
    const requiresValue = (isShown: boolean, type: string) => isShown && isTemplateValueRequired(type)

    // Field order matches the page, so scroll-to-first-error lands on the topmost invalid field.
    return Yup.object({
        name: Yup.string().trim().required(requiredMessage),
        availability: Yup.object({
            weekDays: Yup.array().when("isShown", {
                is: true,
                then: (schema) => schema.min(1, requiredMessage),
            }),
        }),
        title: Yup.object({
            value: Yup.string().when(["isShown", "type"], {
                is: requiresValue,
                then: (schema) => schema.required(requiredMessage),
            }),
        }),
        description: Yup.object({
            value: Yup.string().when(["isShown", "type"], {
                is: requiresValue,
                then: (schema) => schema.required(requiredMessage),
            }),
        }),
        price: Yup.object({
            value: Yup.number()
                .transform((value, original) => (original === "" || original === null ? undefined : value))
                .typeError(requiredMessage)
                .when(["isShown", "type"], {
                    is: requiresValue,
                    then: (schema) => schema.required(requiredMessage),
                }),
        }),
        allergens: Yup.object({
            value: Yup.array().when(["isShown", "type"], {
                is: requiresValue,
                then: (schema) => schema.min(1, requiredMessage),
            }),
        }),
        marketingTag: Yup.object({
            value: Yup.string().when(["isShown", "type"], {
                is: requiresValue,
                then: (schema) => schema.required(requiredMessage),
            }),
        }),
        image: Yup.object({
            photo: Yup.object()
                .nullable()
                .when(["isShown", "type"], {
                    is: requiresValue,
                    then: (schema) => schema.required(requiredMessage),
                }),
        }),
        ingredients: Yup.object({
            value: Yup.string().when(["isShown", "type"], {
                is: requiresValue,
                then: (schema) => schema.required(requiredMessage),
            }),
        }),
        nutrition: Yup.object({
            value: Yup.string().when(["isShown", "type"], {
                is: requiresValue,
                then: (schema) => schema.required(requiredMessage),
            }),
        }),
        co2Footprint: Yup.object({
            value: Yup.number()
                .transform((value, original) => (original === "" || original === null ? undefined : value))
                .typeError(numberMessage)
                .when(["isShown", "type"], {
                    is: requiresValue,
                    then: (schema) => schema.required(requiredMessage),
                }),
        }),
    })
}

export function defaultDishFormValues(): DishFormValues {
    return {
        name: "",
        title: { isShown: true, type: DishParameterType.MANDATORY, value: "", aiFill: true },
        price: { isShown: true, type: DishParameterType.MANDATORY, value: undefined, sameAllWeek: false, aiFill: true },
        description: { isShown: true, type: DishParameterType.MANDATORY, value: "", aiFill: true },
        allergens: { isShown: true, type: DishParameterType.MANDATORY, value: [], aiFill: true },
        // LOCKED, not PREFILLED: the editor folded Prefilled into Locked, and toDishParameterType
        // maps saved PREFILLED back to LOCKED - starting on LOCKED keeps the round-trip symmetric.
        availability: { isShown: true, type: DishParameterType.LOCKED, weekDays: [...WEEKDAYS] },
        marketingTag: { isShown: false, type: DishParameterType.OPTIONAL, value: "", aiFill: true },
        image: { isShown: false, type: DishParameterType.OPTIONAL, photo: undefined },
        ingredients: { isShown: true, type: DishParameterType.OPTIONAL, value: "" },
        nutrition: { isShown: true, type: DishParameterType.OPTIONAL, value: "" },
        co2Footprint: { isShown: true, type: DishParameterType.OPTIONAL, value: "" },
    }
}

function findParameter(dish: MenuTemplateDish, key: DishParameterKey): DishParameter | undefined {
    return dish.parameters.find((parameter) => parameter.key === key)
}

/** The API draft's name for the Locked mode. */
const API_FIXED_TYPE = "FIXED"

export function toDishParameterType(value: DishParameterType | string | undefined): DishParameterType | undefined {
    if (value === undefined) return undefined
    if (value === API_FIXED_TYPE) return DishParameterType.LOCKED
    // The editor no longer offers Prefilled: it collapsed into Locked (same semantics — a template
    // value the kitchen can still edit), so saved Prefilled parameters land on the Locked mode.
    if (value === DishParameterType.PREFILLED) return DishParameterType.LOCKED
    return toEnumValue(DishParameterType, value)
}

// A missing parameter means the input is hidden ("Do not show") — only shown inputs are persisted.
function toFieldConfig(parameter: DishParameter | undefined) {
    return {
        isShown: parameter !== undefined,
        type: toDishParameterType(parameter?.type) ?? DishParameterType.OPTIONAL,
    }
}

export function parseAllergens(defaultValue: string | undefined, allergenOptions: ReadonlyArray<Allergen>): Allergen[] {
    if (!defaultValue) return []
    return defaultValue
        .split(ALLERGEN_SEPARATOR)
        .map((name) => allergenOptions.find((option) => option.name.toLowerCase() === name.trim().toLowerCase()))
        .filter((allergen): allergen is Allergen => allergen !== undefined)
}

function toDishFormValues(dish: MenuTemplateDish, allergenOptions: ReadonlyArray<Allergen>): DishFormValues {
    const title = findParameter(dish, DishParameterKey.TITLE)
    const price = findParameter(dish, DishParameterKey.PRICE)
    const description = findParameter(dish, DishParameterKey.DESCRIPTION)
    const allergens = findParameter(dish, DishParameterKey.ALLERGENS)
    const availability = findParameter(dish, DishParameterKey.AVAILABILITY)
    const marketingTag = findParameter(dish, DishParameterKey.MARKETING_TAG)
    const image = findParameter(dish, DishParameterKey.IMAGE)
    const ingredients = findParameter(dish, DishParameterKey.INGREDIENTS)
    const nutrition = findParameter(dish, DishParameterKey.NUTRITION)
    const co2Footprint = findParameter(dish, DishParameterKey.CO2_FOOTPRINT)

    return {
        name: dish.name,
        availability: {
            ...toFieldConfig(availability),
            weekDays: dish.availability?.weekDays ?? [...WEEKDAYS],
        },
        title: {
            ...toFieldConfig(title),
            value: title?.defaultValue ?? "",
            aiFill: title?.aiFill ?? true,
        },
        price: {
            ...toFieldConfig(price),
            value: price?.defaultValue ? Number(price.defaultValue) : undefined,
            sameAllWeek: price?.sameAllWeek ?? false,
            aiFill: price?.aiFill ?? true,
        },
        description: {
            ...toFieldConfig(description),
            value: description?.defaultValue ?? "",
            aiFill: description?.aiFill ?? true,
        },
        allergens: {
            ...toFieldConfig(allergens),
            value: parseAllergens(allergens?.defaultValue, allergenOptions),
            aiFill: allergens?.aiFill ?? true,
        },
        marketingTag: {
            ...toFieldConfig(marketingTag),
            value: marketingTag?.defaultValue ?? "",
            aiFill: marketingTag?.aiFill ?? true,
        },
        image: {
            ...toFieldConfig(image),
            photo: image?.defaultValue ? { uid: image.defaultValue } : undefined,
        },
        ingredients: {
            ...toFieldConfig(ingredients),
            value: ingredients?.defaultValue ?? "",
        },
        nutrition: {
            ...toFieldConfig(nutrition),
            value: nutrition?.defaultValue ?? "",
        },
        co2Footprint: {
            ...toFieldConfig(co2Footprint),
            value: co2Footprint?.defaultValue ?? "",
        },
    }
}

export function toTemplateFormValues(
    details: MenuTemplateDetails,
    allergenOptions: ReadonlyArray<Allergen>,
): MenuTemplateFormValues {
    return {
        name: details.name,
        channels: details.channels ?? [],
        categories: details.categories.map((category) => ({
            name: category.name,
            dishes: category.dishes.map((dish) => toDishFormValues(dish, allergenOptions)),
        })),
    }
}

type FieldPayloadInput = Readonly<{
    key: DishParameterKey
    config: Readonly<{ isShown: boolean; type: DishParameterType }>
    defaultValue?: string
    sameAllWeek?: boolean
    aiFill?: boolean
}>

function toParameter({ key, config, defaultValue, sameAllWeek, aiFill }: FieldPayloadInput): DishParameter | undefined {
    if (!config.isShown) return undefined
    return {
        key,
        type: config.type,
        defaultValue,
        sameAllWeek,
        aiFill,
    }
}

function toDishPayload(dish: DishFormValues): MenuTemplateDish {
    const parameters = [
        toParameter({
            key: DishParameterKey.TITLE,
            config: dish.title,
            defaultValue: dish.title.value || undefined,
            aiFill: dish.title.aiFill,
        }),
        toParameter({
            key: DishParameterKey.PRICE,
            config: dish.price,
            // The currency field leaves an empty string behind when cleared, which is not a price.
            defaultValue: String(dish.price.value ?? "") || undefined,
            sameAllWeek: dish.price.sameAllWeek,
            aiFill: dish.price.aiFill,
        }),
        toParameter({
            key: DishParameterKey.DESCRIPTION,
            config: dish.description,
            defaultValue: dish.description.value || undefined,
            aiFill: dish.description.aiFill,
        }),
        toParameter({
            key: DishParameterKey.ALLERGENS,
            config: dish.allergens,
            defaultValue: dish.allergens.value.map((allergen) => allergen.name).join(ALLERGEN_SEPARATOR) || undefined,
            aiFill: dish.allergens.aiFill,
        }),
        toParameter({ key: DishParameterKey.AVAILABILITY, config: dish.availability }),
        toParameter({
            key: DishParameterKey.MARKETING_TAG,
            config: dish.marketingTag,
            defaultValue: dish.marketingTag.value || undefined,
            aiFill: dish.marketingTag.aiFill,
        }),
        toParameter({ key: DishParameterKey.IMAGE, config: dish.image, defaultValue: dish.image.photo?.uid }),
        toParameter({
            key: DishParameterKey.INGREDIENTS,
            config: dish.ingredients,
            defaultValue: dish.ingredients.value || undefined,
        }),
        toParameter({
            key: DishParameterKey.NUTRITION,
            config: dish.nutrition,
            defaultValue: dish.nutrition.value || undefined,
        }),
        toParameter({
            key: DishParameterKey.CO2_FOOTPRINT,
            config: dish.co2Footprint,
            defaultValue: dish.co2Footprint.value || undefined,
        }),
    ].filter((parameter): parameter is DishParameter => parameter !== undefined)

    return {
        name: dish.name,
        availability: { weekDays: dish.availability.weekDays },
        parameters,
    }
}

function toCategoryPayload(category: CategoryFormValues): MenuTemplateCategory {
    return {
        name: category.name,
        dishes: category.dishes.map(toDishPayload),
    }
}

export function toTemplatePayload(values: MenuTemplateFormValues): SaveMenuTemplatePayload {
    return {
        name: values.name,
        channels: values.channels,
        categories: values.categories.map(toCategoryPayload),
    }
}

type DishFieldEntry = Readonly<{
    key: DishParameterKey
    config: Readonly<{ isShown: boolean; type: DishParameterType }>
}>

function dishFieldEntries(dish: DishFormValues): ReadonlyArray<DishFieldEntry> {
    return [
        { key: DishParameterKey.AVAILABILITY, config: dish.availability },
        { key: DishParameterKey.TITLE, config: dish.title },
        { key: DishParameterKey.DESCRIPTION, config: dish.description },
        { key: DishParameterKey.PRICE, config: dish.price },
        { key: DishParameterKey.ALLERGENS, config: dish.allergens },
        { key: DishParameterKey.MARKETING_TAG, config: dish.marketingTag },
        { key: DishParameterKey.IMAGE, config: dish.image },
        { key: DishParameterKey.INGREDIENTS, config: dish.ingredients },
        { key: DishParameterKey.NUTRITION, config: dish.nutrition },
        { key: DishParameterKey.CO2_FOOTPRINT, config: dish.co2Footprint },
    ]
}

function aiOffLabels(dish: DishFormValues, l10n: AppLocalization): string[] {
    const aiEntries = [
        { key: DishParameterKey.TITLE, config: dish.title },
        { key: DishParameterKey.PRICE, config: dish.price },
        { key: DishParameterKey.DESCRIPTION, config: dish.description },
        { key: DishParameterKey.ALLERGENS, config: dish.allergens },
        { key: DishParameterKey.MARKETING_TAG, config: dish.marketingTag },
    ]
    return aiEntries
        .filter((entry) => entry.config.isShown && !entry.config.aiFill)
        .map((entry) => l10n.getStringForEnum("merchant-menu-templates-field", entry.key, entry.key))
}

export type DishSummary = Readonly<{
    /** Which inputs the kitchen must fill in the week menu, or a note that there are none. */
    mandatoryLine: string
    /** Inputs the week menu's AI generation must leave alone. */
    aiOffLine?: string
}>

export function dishSummary(dish: DishFormValues, l10n: AppLocalization): DishSummary {
    const mandatoryLabels = dishFieldEntries(dish)
        .filter((entry) => entry.config.isShown && entry.config.type === DishParameterType.MANDATORY)
        .map((entry) => l10n.getStringForEnum("merchant-menu-templates-field", entry.key, entry.key))

    const mandatoryMode = l10n.getStringForEnum(
        "merchant-menu-templates-mode",
        DishParameterType.MANDATORY,
        DishParameterType.MANDATORY,
    )
    const aiOff = aiOffLabels(dish, l10n)

    return {
        mandatoryLine: mandatoryLabels.length
            ? `${mandatoryMode}: ${mandatoryLabels.join(", ")}`
            : l10n.getString("merchant-menu-templates-dish-no-mandatory-fields"),
        aiOffLine: aiOff.length
            ? `${l10n.getString("merchant-menu-templates-ai-off-label")}: ${aiOff.join(", ")}`
            : undefined,
    }
}
